import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  clearDraftFromStorage,
  loadDraftFromStorage,
  saveDraftToStorage,
} from "../utils/contentLoader";
import { applyDraftViaApi, checkContentApi } from "../admin/contentApi";
import { mergeDraftWithSite } from "./buildSiteDraft";

const StudioContext = createContext(null);

const STUDIO_QUERY = "studio";

export const isStudioEnabled = () => process.env.NODE_ENV === "development";

export const StudioProvider = ({ children }) => {
  const enabled = isStudioEnabled();
  const [draft, setDraft] = useState(() =>
    enabled ? mergeDraftWithSite(loadDraftFromStorage()) : null,
  );
  const [studioMode, setStudioMode] = useState(false);
  const [activeRegion, setActiveRegion] = useState(null);
  const [status, setStatus] = useState("");
  const [apiOnline, setApiOnline] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(STUDIO_QUERY) === "1") setStudioMode(true);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !studioMode) return undefined;
    let cancelled = false;
    const ping = async () => {
      const ok = await checkContentApi();
      if (!cancelled) setApiOnline(ok);
    };
    ping();
    const id = setInterval(ping, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, studioMode]);

  /** Rebuild draft from current src/data (+ optional browser storage). */
  const refreshDraftFromCode = useCallback((options = {}) => {
    const { clearBrowserDraft = false } = options;
    if (clearBrowserDraft) clearDraftFromStorage();
    const stored = clearBrowserDraft ? null : loadDraftFromStorage();
    const next = mergeDraftWithSite(stored);
    setDraft(next);
    setDirty(false);
    return { next, hadBrowserDraft: Boolean(stored) };
  }, []);

  const enterStudio = useCallback(() => {
    // Re-merge on enter so Visual edit picks up code/HMR changes instead of a
    // stale in-memory snapshot — unless the user has unsaved panel edits.
    if (!dirtyRef.current) {
      const { hadBrowserDraft } = refreshDraftFromCode({
        clearBrowserDraft: false,
      });
      if (hadBrowserDraft) {
        setStatus(
          "Browser draft loaded (may override recent code). Use Reset from code if stale.",
        );
      }
    }
    setStudioMode(true);
    const url = new URL(window.location.href);
    url.searchParams.set(STUDIO_QUERY, "1");
    window.history.replaceState({}, "", url);
  }, [refreshDraftFromCode]);

  const exitStudio = useCallback(() => {
    setStudioMode(false);
    setActiveRegion(null);
    const url = new URL(window.location.href);
    url.searchParams.delete(STUDIO_QUERY);
    window.history.replaceState({}, "", url);
  }, []);

  const resetFromCode = useCallback(() => {
    refreshDraftFromCode({ clearBrowserDraft: true });
    setActiveRegion(null);
    setStatus("Reset to current code (browser draft cleared).");
  }, [refreshDraftFromCode]);

  const updateDraft = useCallback((updater) => {
    setDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
    setDirty(true);
    setStatus("");
  }, []);

  const saveLocal = useCallback(() => {
    saveDraftToStorage(draft);
    setDirty(false);
    setStatus("Draft saved to browser storage.");
  }, [draft]);

  const saveAndApply = useCallback(async () => {
    setApplying(true);
    setStatus("");
    try {
      saveDraftToStorage(draft);
      const result = await applyDraftViaApi(draft);
      setDirty(false);
      setStatus(result.message || "Applied. Review git diff, then commit.");
    } catch (err) {
      setStatus(err.message || "Apply failed.");
    } finally {
      setApplying(false);
    }
  }, [draft]);

  const value = useMemo(
    () => ({
      enabled,
      studioMode,
      draft,
      activeRegion,
      status,
      apiOnline,
      applying,
      dirty,
      enterStudio,
      exitStudio,
      resetFromCode,
      setActiveRegion,
      updateDraft,
      setDraft,
      saveLocal,
      saveAndApply,
    }),
    [
      enabled,
      studioMode,
      draft,
      activeRegion,
      status,
      apiOnline,
      applying,
      dirty,
      enterStudio,
      exitStudio,
      resetFromCode,
      updateDraft,
      saveLocal,
      saveAndApply,
    ],
  );

  if (!enabled) return children;

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  );
};

export const useStudio = () => {
  const ctx = useContext(StudioContext);
  if (!ctx) {
    return {
      enabled: false,
      studioMode: false,
      draft: null,
      activeRegion: null,
      enterStudio: () => {},
      exitStudio: () => {},
      resetFromCode: () => {},
      setActiveRegion: () => {},
      updateDraft: () => {},
    };
  }
  return ctx;
};

/** Prefer draft overlay while Studio is open (or always in dev if draft exists for text). */
export const useStudioField = (path, fallback) => {
  const { studioMode, draft } = useStudio();
  if (!studioMode || !draft) return fallback;
  const parts = path.split(".");
  let cur = draft;
  for (const part of parts) {
    if (cur == null) return fallback;
    cur = cur[part];
  }
  return cur == null ? fallback : cur;
};
